/*
 * addressAutocomplete — Google Places suggestions on the Site Address field
 * (MOHO-149).
 *
 * Enhances the plain <input> the server already rendered. It does NOT replace
 * it: the input keeps its name, id, value, label, hint and error wiring, so
 * without JS — or with no key configured — the field submits exactly as it did
 * before this component existed. Only the suggestion list is added.
 *
 * WHY THE REST ENDPOINT AND NOT THE MAPS JS API
 *
 * The obvious route is loading `libraries=places` through lib/google-maps.js
 * and dropping in Google's <gmp-place-autocomplete> element. That element
 * renders its OWN input, so it would take the place of the .mh-field shell and
 * lose the floating label, the error state and the `name` the form posts under
 * — the address would stop reaching HubSpot while the field still looked
 * right. The Maps JS API is also a large bundle for one text field. Fetching
 * JSON and building our own listbox keeps the markup, the styling and the ARIA
 * ours for well under a kilobyte.
 *
 * WHY THE KEY IS IN THE PAGE
 *
 * It is a browser key, restricted by referrer, which is the only kind Google
 * accepts from a browser — the same arrangement the map islands already use
 * for their data-key. The referrer list registered against it is the guardrail,
 * not secrecy. Never render the geocoding key here: that one is
 * application-unrestricted, which makes it a bearer credential.
 *
 * WHY NO SESSION TOKEN AND NO PLACE DETAILS
 *
 * The prediction text is already a complete formatted address, which is all
 * the sales team needs. Calling Place Details to re-fetch it would add $5 per
 * 1,000 for a string we hold. Session tokens only pay off when a session ends
 * in a Place Details call — an abandoned one reverts to per-request pricing
 * either way — so a token here would buy nothing.
 *
 * COST GUARDS
 *
 * Google bills per REQUEST, not per address, against a 10,000/month free
 * allowance. Nothing is requested until someone types, and the field is
 * display:none until `own_land` is answered Yes, so bots and "No" answers cost
 * nothing. MIN_CHARS holds the first request back until the input is worth
 * sending, and DEBOUNCE_MS collapses a burst of keystrokes into one.
 * MAX_REQUESTS is the backstop for a stuck key or a scripted page — it caps
 * one field's spend for the life of the page rather than trusting the other
 * two.
 */

const ENDPOINT = 'https://places.googleapis.com/v1/places:autocomplete';

// Below this, a query is too vague to return a useful address and is not
// worth a billed request.
const MIN_CHARS = 4;

// Long enough to collapse normal typing into one request, short enough that
// the list still feels immediate.
const DEBOUNCE_MS = 300;

// Backstop only — normal use of one field is a handful of requests.
const MAX_REQUESTS = 60;

export default function addressAutocomplete() {
    return {
        /*
         * Every key is declared here rather than first assigned in init().
         * Alpine resolves `this` through a merge proxy over the whole x-data
         * stack, so a write to a key no scope owns yet lands on the OUTERMOST
         * one — inside the form modal that is the shared formModal scope, and
         * three pre-rendered forms would then trample each other's state.
         * conditional-field.js carries the same note and the same fix
         * (MOHO-141).
         */
        open: false,
        activeIndex: -1,
        statusMessage: '',
        suggestions: [],
        apiKey: '',
        listId: '',
        requests: 0,
        timer: null,
        controller: null,
        skipNextInput: false,

        get expandedAttr() {
            return String(this.open);
        },

        get activeDescendant() {
            if (!this.open || this.activeIndex < 0) {
                return null;
            }

            return `${this.listId}-${this.activeIndex}`;
        },

        init() {
            this.apiKey = this.$el.dataset.autocompleteKey || '';
            this.listId = this.$refs.list ? this.$refs.list.id : '';

            // No key configured. Hand the field back to the browser: strip the
            // combobox roles the server rendered, so assistive tech is not told
            // to expect a listbox that will never fill.
            if (!this.apiKey) {
                ['role', 'aria-autocomplete', 'aria-controls', 'aria-expanded'].forEach(
                    (attribute) => this.$refs.input.removeAttribute(attribute),
                );

                return;
            }

            // The browser's own address autofill panel overlaps ours and wins
            // the pointer. Turned off only once we know we can replace it —
            // the server-rendered `autocomplete="street-address"` is what a
            // no-JS visitor keeps.
            this.$refs.input.setAttribute('autocomplete', 'off');
        },

        destroy() {
            clearTimeout(this.timer);
            this.abort();
        },

        abort() {
            if (this.controller) {
                this.controller.abort();
                this.controller = null;
            }
        },

        onInput() {
            // commit() writes the value and dispatches a native `input` so the
            // rest of the form (conditionalField's delegated listener, any
            // validation) sees the change. That event lands back here, and
            // acting on it would fetch suggestions for the address we just
            // accepted.
            if (this.skipNextInput) {
                this.skipNextInput = false;

                return;
            }

            clearTimeout(this.timer);

            const query = this.$refs.input.value.trim();

            if (query.length < MIN_CHARS) {
                this.abort();
                this.close();

                return;
            }

            this.timer = setTimeout(() => this.search(query), DEBOUNCE_MS);
        },

        async search(query) {
            if (!this.apiKey || this.requests >= MAX_REQUESTS) {
                return;
            }

            this.abort();
            this.controller = new AbortController();
            this.requests += 1;

            try {
                const response = await fetch(ENDPOINT, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': this.apiKey,
                    },
                    body: JSON.stringify({
                        input: query,
                        // MH builds in NSW, so anything outside Australia is
                        // noise. This also keeps the list short enough to scan.
                        includedRegionCodes: ['au'],
                        // Table B types, which is what Autocomplete (New)
                        // accepts here. NOT 'address' — that was the legacy
                        // Autocomplete's type collection and the new API
                        // rejects the whole request with 400 INVALID_ARGUMENT,
                        // so the field returns nothing at all. Four types
                        // rather than one because a block being built on may be
                        // a numbered address, a unit, a named property, or a
                        // street with no number yet.
                        includedPrimaryTypes: [
                            'street_address',
                            'subpremise',
                            'premise',
                            'route',
                        ],
                    }),
                    signal: this.controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`Places responded ${response.status}`);
                }

                const body = await response.json();

                this.render(
                    (body.suggestions || [])
                        .map((entry) => entry.placePrediction?.text?.text)
                        .filter(Boolean),
                );
            } catch (error) {
                // An aborted request is the expected outcome of typing on, not
                // a failure. Anything else — a refused key, a Google outage, a
                // blocked request — leaves the field as a plain text input. A
                // broken suggestion list must never block an enquiry.
                if (error.name !== 'AbortError') {
                    this.close();
                }
            }
        },

        render(items) {
            this.suggestions = items;
            this.activeIndex = -1;

            const list = this.$refs.list;
            list.replaceChildren();

            if (items.length === 0) {
                this.statusMessage = 'No matching addresses.';
                this.open = false;

                return;
            }

            items.forEach((text, index) => {
                const option = document.createElement('li');

                option.id = `${this.listId}-${index}`;
                option.className = 'mh-field__suggestion';
                option.setAttribute('role', 'option');
                option.setAttribute('aria-selected', 'false');
                option.textContent = text;
                // Options are built here rather than server-rendered, so the
                // listener goes on directly. Alpine's CSP build cannot parse an
                // inline expression to pass the index, and the element is
                // replaced wholesale on the next search, so there is nothing to
                // detach.
                option.addEventListener('click', () => this.commit(index));

                list.appendChild(option);
            });

            this.statusMessage = `${items.length} address ${items.length === 1 ? 'suggestion' : 'suggestions'} available.`;
            this.open = true;
        },

        move(step) {
            if (!this.open || this.suggestions.length === 0) {
                return;
            }

            const count = this.suggestions.length;
            this.activeIndex = (this.activeIndex + step + count) % count;

            Array.from(this.$refs.list.children).forEach((option, index) => {
                option.setAttribute(
                    'aria-selected',
                    index === this.activeIndex ? 'true' : 'false',
                );
            });

            this.$refs.list.children[this.activeIndex].scrollIntoView({
                block: 'nearest',
            });
        },

        focusNext() {
            this.move(1);
        },

        focusPrev() {
            this.move(-1);
        },

        onEnter(event) {
            // Only swallow Enter when it is choosing a suggestion. With the
            // list closed, or with nothing highlighted, Enter must still submit
            // the form — a typed address that matched nothing is a valid
            // answer, because the field is nullable free text.
            if (!this.open || this.activeIndex < 0) {
                return;
            }

            event.preventDefault();
            this.commit(this.activeIndex);
        },

        commit(index) {
            const chosen = this.suggestions[index];

            if (!chosen) {
                return;
            }

            this.$refs.input.value = chosen;
            this.skipNextInput = true;
            this.$refs.input.dispatchEvent(new Event('input', { bubbles: true }));
            this.$refs.input.dispatchEvent(new Event('change', { bubbles: true }));
            this.$refs.input.focus();
            this.close();
        },

        close() {
            this.open = false;
            this.activeIndex = -1;
            this.statusMessage = '';
        },
    };
}
